import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuditLogEntry } from '../utils/auditLog';

interface StudentsListProps {
  auditEntries: AuditLogEntry[];
  isHebrew: boolean;
}

interface StudentInfo {
  userId: string;
  userName: string;
  userEmail?: string;
  lastActivity?: Date;
  playCount: number;
}

const StudentsList: React.FC<StudentsListProps> = ({ auditEntries, isHebrew }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Get unique students with aggregated info
  const students = useMemo(() => {
    const studentMap = new Map<string, StudentInfo>();

    auditEntries.forEach((entry) => {
      if (!entry.userId) return;

      const existing = studentMap.get(entry.userId);
      const entryDate = entry.createdAt?.toDate ? entry.createdAt.toDate() : new Date();

      if (existing) {
        existing.playCount += 1;
        if (!existing.lastActivity || entryDate > existing.lastActivity) {
          existing.lastActivity = entryDate;
        }
      } else {
        studentMap.set(entry.userId, {
          userId: entry.userId,
          userName: entry.userName || t('anonymous') || 'Anonymous',
          userEmail: entry.userEmail,
          lastActivity: entryDate,
          playCount: 1,
        });
      }
    });

    // Convert to array and sort by last activity (most recent first)
    return Array.from(studentMap.values()).sort((a, b) => {
      if (!a.lastActivity && !b.lastActivity) return 0;
      if (!a.lastActivity) return 1;
      if (!b.lastActivity) return -1;
      return b.lastActivity.getTime() - a.lastActivity.getTime();
    });
  }, [auditEntries, t]);

  const handleStudentClick = (studentId: string) => {
    navigate(`/student-details/${studentId}`);
  };

  const formatDate = (date?: Date) => {
    if (!date) return '';
    return date.toLocaleDateString(isHebrew ? 'he-IL' : 'en-US', {
      day: 'numeric',
      month: 'short',
    });
  };

  if (students.length === 0) {
    return (
      <div
        dir={isHebrew ? 'rtl' : 'ltr'}
        className="bg-white/70 backdrop-blur-sm rounded-lg px-4 py-6 text-center"
      >
        <p className="text-sm text-gray-500">{t('no_students_yet') || 'No students yet'}</p>
      </div>
    );
  }

  return (
    <div dir={isHebrew ? 'rtl' : 'ltr'}>
      <div className="flex items-center gap-2 mb-2">
        <span className="text-base">👥</span>
        <h4 className="text-xs font-bold" style={{ color: 'var(--kol-gold-dark, #8B7532)' }}>
          {t('all_students') || 'All Students'} ({students.length})
        </h4>
      </div>
      <div className="space-y-1.5 max-h-96 overflow-y-auto">
        {students.map((student) => (
          <button
            key={student.userId}
            onClick={() => handleStudentClick(student.userId)}
            className="w-full flex items-center gap-2 rounded-lg px-3 py-2 shadow-sm hover:scale-[1.02] transition-all text-left"
            style={{
              background: 'rgba(255, 255, 255, 0.9)',
              border: '1px solid var(--kol-gray-200, #E4E7EB)',
              boxShadow: 'var(--kol-shadow-sm, 0 2px 4px rgba(10, 22, 40, 0.08))',
            }}
            dir={isHebrew ? 'rtl' : 'ltr'}
          >
            <div
              className="w-7 h-7 rounded-full flex items-center justify-center text-white font-bold text-[10px] shadow flex-shrink-0"
              style={{ background: 'var(--kol-navy, #132A49)' }}
            >
              {student.userName?.[0]?.toUpperCase() || '?'}
            </div>
            <div className="flex-1 min-w-0">
              <div
                className="text-xs font-semibold truncate"
                style={{ color: 'var(--kol-navy, #132A49)' }}
              >
                {student.userName}
              </div>
              <div className="text-[10px]" style={{ color: 'var(--kol-gray-500, #6B7C93)' }}>
                {student.playCount}{' '}
                {student.playCount === 1
                  ? t('play_count_singular') || 'play'
                  : t('play_count_plural') || 'plays'}
                {student.lastActivity &&
                  ` · ${t('last_active') || 'Last active'} ${formatDate(student.lastActivity)}`}
              </div>
            </div>
            <div className="text-[10px] text-[#9ca3af] flex-shrink-0">→</div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default StudentsList;
